import * as React from 'react'
import {ReactElement, ReactNode} from 'react'
import {Nullable} from '../../../../app/TypeHelpers'
import NatMenuMasterRepo from '../repository/NatMenuMasterRepo'
import styled from 'styled-components'
import {Colors, DisplaySizeValue, FontWeights} from '../../../../styledComponents/Constants'
import {MenuData, NatGetMenuMasterAuthedAdmin} from '../repository/NatGetMenuMasterAuthedAdmin'
import {
    AsyncCommunicationProps,
    withAsyncCommunication,
} from '../../../../components/shared/hoc/withAsyncCommunication'
import EventEmitterExAdmin from '../../../../components/shared/pageBaseComponents/EventEmitterExAdmin'
import {KeyboardArrowDownOutlined} from '@mui/icons-material'
import {Box, Button, ClickAwayListener} from '@mui/material'
import {generateMedia} from 'styled-media-query'
import {AdminHeaderShadowCss} from '../../../../components/header/AdminHeaderAreaParts'
import {isMobile} from 'react-device-detect'
import {RouterPageProps} from '../../../../components/shared/RouterPropsInjector'

const ADMIN_MENU_HEADER_Z_INDEX = 900

const MENU_BUTTON_AVERAGE_WIDTH = 90
const MENU_BLANK_WIDTH = 230

const menuMedia = generateMedia({
    small: `${DisplaySizeValue.AdminWidthMin}px`,
})

interface Props {
    menuMasterRepo: NatMenuMasterRepo
}

interface States {
    openedMenuIndex: number | false
    menuDataList: Nullable<MenuData[]>
    isLoadCompleted: boolean
}

interface ExternalProps {
    eventEmitter: EventEmitterExAdmin
}

class NatAdminMenuMasterComponentPc
    extends React.Component<Props & AsyncCommunicationProps & RouterPageProps & ExternalProps, States> {

    constructor(props: Props & AsyncCommunicationProps & RouterPageProps & ExternalProps) {
        super(props)
        this.state = {
            openedMenuIndex: false,
            menuDataList: null,
            isLoadCompleted: false,
        }
    }

    async componentDidMount(): Promise<void> {
        const menuMasterAdmin: Nullable<NatGetMenuMasterAuthedAdmin> = await this.props.asyncCallFunction(
            this.props.menuMasterRepo.getMenuMasterAuthedAdmin.bind(this.props.menuMasterRepo),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
            },
        )

        if (menuMasterAdmin && menuMasterAdmin.details) {
            this.setState({
                menuDataList: menuMasterAdmin.details,
                isLoadCompleted: true,
            })
        }
    }

    closeMenu = () => {
        this.setState({openedMenuIndex: false})
    }

    handleClickAwaySubMenu = () => {
        this.closeMenu()
    }

    handleClickMenuButton = (menuIndex: number) => () => {
        if (this.state.openedMenuIndex === menuIndex) {
            this.closeMenu()
        } else {
            this.setState({openedMenuIndex: menuIndex})
        }
    }

    handleClickMenuItem = (url: string) => () => {
        this.closeMenu()
        this.props.navigate(url)
    }

    // TODO DBのメニュー定義が変更されたら見直す
    getMenuTitle(menuTitle: string): ReactElement {
        if (menuTitle === 'マンション/組合情報') {
            return <>
                &emsp;マンション&emsp;<br/>
                組合情報
            </>
        } else if (menuTitle === '情報交換掲示板') {
            return <>
                &emsp;情報交換&emsp;<br/>
                掲示板
            </>
        } else if (menuTitle === '組合員資格変更届') {
            return <>
                &emsp;組合員資格&emsp;<br/>
                変更届
            </>
        }

        const trimmedTitle = menuTitle
        if (trimmedTitle.length === 2) {
            return <>
                &emsp;&emsp;{trimmedTitle}&emsp;&emsp;
            </>
        } else if (trimmedTitle.length === 3) {
            return <>
                &emsp;&ensp;{trimmedTitle}&ensp;&emsp;
            </>
        } else {
            return <>
                &emsp;{trimmedTitle}&emsp;
            </>
        }
    }

    renderMenu(
        menuData: MenuData,
        index: number,
        numOfMenus: number,
    ): ReactNode {
        const isOpen = this.state.openedMenuIndex === index

        return (
            <MenuContainer
                key={`admin-header-menu-${index}`}
                $menuOpened={isOpen}
            >
                <MenuButton
                    disableRipple={true}
                    onClick={this.handleClickMenuButton(index)}
                    $menuOpened={isOpen}
                    $numOfMenus={numOfMenus}
                >
                    <MenuTitle>
                        {this.getMenuTitle(menuData.menuName)}
                    </MenuTitle>
                    <ArrowDownIcon/>
                </MenuButton>
                <MenuArea $open={isOpen}>
                    {menuData.childMenuInfo && menuData.childMenuInfo.map((childMenu, childIndex) => (
                        <MenuItem
                            onClick={this.handleClickMenuItem(childMenu.url)}
                            key={`admin-header-menu-${index}-${childIndex}`}
                        >
                            {childMenu.menuName}
                        </MenuItem>
                    ))}
                </MenuArea>
            </MenuContainer>
        )
    }

    render(): Nullable<ReactElement> {
        const {openedMenuIndex, menuDataList, isLoadCompleted} = this.state
        if (!isLoadCompleted || menuDataList === null) {
            return null
        }

        const isMenuOpened = openedMenuIndex !== false

        return <>
            <GlobalMenuArea>
                {/* メニュー間での干渉を避けるためメニューすべてを1つのClickAwayListenerで包む */}
                {/* メニューが閉じている間はonClickAwayイベントが発火しないようにevent設定を切り替え */}
                <ClickAwayListener
                    onClickAway={this.handleClickAwaySubMenu}
                    mouseEvent={isMenuOpened ? 'onClick' : false}
                    touchEvent={isMenuOpened ? 'onTouchEnd' : false}
                >
                    <MenusContainer>
                        {menuDataList.map((menuData, index) => (
                            this.renderMenu(menuData, index, menuDataList.length)
                        ))}
                    </MenusContainer>
                </ClickAwayListener>
                <Box width={`${MENU_BLANK_WIDTH}px`}/>
            </GlobalMenuArea>
        </>
    }
}

export default withAsyncCommunication(NatAdminMenuMasterComponentPc)

const GlobalMenuArea = styled.div`
  display: flex;
  box-sizing: border-box;
  background-color: ${Colors.Surface};
  position: relative;
  z-index: ${ADMIN_MENU_HEADER_Z_INDEX};
  width: 100%;
  line-height: 1.5;
`

const MenusContainer = styled.div`
  display: flex;
  width: fit-content;
`

const MenuContainer = styled.div<{ $menuOpened: boolean }>`
  ${props => props.$menuOpened && `
    z-index: 1;
  `}
`

const MenuButton = styled(Button)<{ $menuOpened: boolean, $numOfMenus: number }>`
  &.MuiButton-root {
    border-radius: 0;
    padding: 8px 0 4px;
    width: 100%;
    color: ${Colors.Body};
    font-weight: ${FontWeights.Bold};
    line-height: 1.5;

    // メニューの拡大縮小に合わせて文字サイズも10~14pxで変動
    // (MENU_BUTTON_WIDTH * props.numOfMenus + MENU_BLANK_WIDTH) / 14  は
    // メニュー表示部分の理想の横幅を14pxの文字で埋めた時の文字数
    font-size: clamp(10px, ${props => 100 / ((MENU_BUTTON_AVERAGE_WIDTH * props.$numOfMenus + MENU_BLANK_WIDTH) / 14)}vw, 14px);
    // 管理画面のminWidthより小さくなったら文字サイズを固定する
    ${props => menuMedia.lessThan('small')`
      font-size: clamp(10px, ${DisplaySizeValue.AdminWidthMin / ((MENU_BUTTON_AVERAGE_WIDTH * props.$numOfMenus + MENU_BLANK_WIDTH) / 14)}px, 14px);
    `}
    // スマホのときは画面横幅に余裕があり文字サイズを縮める必要がないので14pxで固定
    // iPhoneではvwがViewPortの横幅でなくスマホの画面サイズを参照し、上記の文字サイズを小さくする処理が誤作動するため、この指定が必要
    ${isMobile && `
      font-size: 14px;
    `}

    ${props => props.$menuOpened ? `
      background-color: #b2b9e1;
      @media(hover: hover) {
        :hover {
          background-color: #b2b9e1;
        }
      }
    ` : `
      background-color: ${Colors.Surface};
      @media(hover: hover) {
        :hover {
          background-color: #e2e5f4;
        }
      }
    `}
  }

  display: flex;
  flex-direction: column;
  align-items: center;
`

const MenuTitle = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 42px;
  text-align: center;
  white-space: nowrap;
`

const ArrowDownIcon = styled(KeyboardArrowDownOutlined)`
  &.MuiSvgIcon-root {
    color: ${Colors.AccentDark};
    font-size: 16px;
  }
`

const MenuArea = styled.div<{ $open: boolean }>`
  position: absolute;
  background-color: ${Colors.Surface};
  overflow: hidden;
  ${AdminHeaderShadowCss};
  z-index: 0;
  width: 240px;

  ${(props) => props.$open ? `
    transition: all 0.5s;
    opacity: 1;
    visibility: visible;
  ` : `
    opacity: 0;
    visibility: hidden;
  `}
`

const MenuItem = styled.div`
  padding: 16px 0 16px 24px;
  color: ${Colors.Body};
  font-size: 14px;
  white-space: nowrap;
  cursor: pointer;

  :hover {
    background-color: #e2e5f4;
  }
`
